import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Typography,
  Modal,
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  useEditUserControllerEditUserMutation,
  useGetUsersListControllerGetUsersListQuery,
  UserDto,
  UserRoles,
} from '@shared/services/apiService/apiService';
import { SuperAdminAuthenticatedPath } from '@shared/constants/routes.constants';
import { UsersTabContent } from './tabs/UsersTabContent';
import { AgentProfilesTabContent } from './tabs/AgentProfilesTabContent';
import { CreateUserDialog } from './tabs/CreateUserDialog';
import { useTranslation } from 'react-i18next';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import CustomOutlinedInput from '@shared/components/forms/theme-elements/CustomOutlinedInput';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { passwordRules, PASSWORD_MAX_LENGTH } from '@shared/constants/validation.constants';

// COLUMN_NAMES_WITH_PASSWORD will be constructed using translations

const FIELD_NAMES = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  PASSWORD: 'password',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const EditCompanyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [companyId, setCompanyId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<UserRoles>(UserRolesEnum.ADMIN);
  const [tabIndex, setTabIndex] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    data: users,
    refetch: refetchUsers,
    isLoading,
  } = useGetUsersListControllerGetUsersListQuery({ id: companyId }, { skip: !companyId });

  const [useEditUser] = useEditUserControllerEditUserMutation();

  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get('id');
    const name = searchParams.get('name');

    if (id) {
      setCompanyId(id);
    }

    if (name) {
      setCompanyName(name);
    }
  }, [searchParams]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserDto) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
    setUserRole(user.role ?? UserRolesEnum.ADMIN);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setErrors({});
    setShowPassword(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSelectedUser({
      ...selectedUser,
      [name]: value,
    });
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return passwordRules.required as string;
    }
    if (password.length < passwordRules.minLength.value) {
      return passwordRules.minLength.message;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      return passwordRules.maxLength.message;
    }
    const validationResult = passwordRules.validate(password, selectedUser);
    if (typeof validationResult === 'string') {
      return validationResult;
    }
    return undefined;
  };

  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};

    // Validate password if it's provided
    if (selectedUser?.password) {
      const passwordError = validatePassword(selectedUser.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const updatedUser = {
      id: selectedUser.id,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      email: selectedUser.email,
      role: userRole,
      password: selectedUser.password,
    };

    try {
      await useEditUser({ id: updatedUser.id, editUserRequestDto: updatedUser }).unwrap();
      try {
        localStorage.setItem('call-agent:user-role-updated-at', String(Date.now()));
      } catch {
        // Ignore storage quota / privacy mode errors
      }
      setIsModalOpen(false);
      setErrors({});
      refetchUsers();
    } catch (error) {
      setErrors((error as any).data);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const usersTabContent = (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={() => setIsCreateUserDialogOpen(true)}>
          {t('superAdminMainPage.companies.operation.addUser')}
        </Button>
      </Box>
      <UsersTabContent
        users={users}
        isLoading={isLoading}
        anchorEl={anchorEl}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
        handleEditClick={handleEditClick}
      />
    </Box>
  );

  return (
    <Box>
      <Box marginY={4}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
          <Link
            to={SuperAdminAuthenticatedPath.COMPANIES}
            style={{
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
            }}>
            {t('superAdminMainPage.menuItems.companies')}
          </Link>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
            /
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t('superAdminMainPage.companies.editCompany.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
            /
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>
            {companyName}
          </Typography>
        </Box>
      </Box>
      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="basic tabs example">
        <Tab label={t('superAdminMainPage.companies.tabs.users')} {...a11yProps(0)} />
        <Tab label={t('superAdminMainPage.companies.tabs.agentProfiles')} {...a11yProps(0)} />
      </Tabs>
      <TabPanel value={tabIndex} index={0}>
        {usersTabContent}
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        {companyId && <AgentProfilesTabContent companyId={companyId} />}
      </TabPanel>
      <CreateUserDialog
        open={isCreateUserDialogOpen}
        onClose={() => setIsCreateUserDialogOpen(false)}
        companyId={companyId}
        onSuccess={refetchUsers}
      />
      <Modal open={isModalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}>
          <Typography variant="h6" component="h2">
            {t('superAdminMainPage.companies.editCompany.editUser.title')}
          </Typography>
          {Object.entries(FIELD_NAMES).map(([fieldKey, fieldName]) => {
            const getLabel = () => {
              switch (fieldKey) {
                case 'FIRST_NAME':
                  return t('superAdminMainPage.companies.editCompany.editUser.firstName');
                case 'LAST_NAME':
                  return t('superAdminMainPage.companies.editCompany.editUser.lastName');
                case 'EMAIL':
                  return t('superAdminMainPage.companies.userTableHeaders.email');
                case 'PASSWORD':
                  return t('superAdminMainPage.companies.editCompany.editUser.password');
                default:
                  return '';
              }
            };
            const isPasswordField = fieldName === 'password';

            return (
              <TextField
                key={fieldKey}
                margin="normal"
                fullWidth
                label={getLabel()}
                name={fieldName}
                type={isPasswordField && !showPassword ? 'password' : 'text'}
                value={selectedUser?.[fieldName] || ''}
                onChange={handleInputChange}
                error={Boolean(errors?.[fieldName])}
                helperText={errors?.[fieldName]}
                InputProps={
                  isPasswordField
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={t('common.togglePasswordVisibility')}
                              onClick={() => setShowPassword((show) => !show)}
                              edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            );
          })}
          <FormControl margin="normal" sx={{ minWidth: '100%' }} size="medium">
            <InputLabel id="role-select-label">{t('adminMainPage.agents.role')}</InputLabel>
            <CustomSelect
              labelId="role-select-label"
              onChange={(event: SelectChangeEvent) => setUserRole(event.target.value as UserRoles)}
              id="role-select-id"
              disabled={false}
              value={userRole || selectedUser?.role}
              label={t('adminMainPage.agents.role')}
              input={
                <CustomOutlinedInput label={t('superAdminMainPage.companies.profiles.name')} />
              }
              renderValue={(value: string) => {
                if (!value) {
                  return null;
                }

                return <>{value}</>;
              }}>
              <MenuItem key={UserRolesEnum.VIEWER} value={UserRolesEnum.VIEWER}>
                {UserRolesEnum.VIEWER}
              </MenuItem>
              <MenuItem key={UserRolesEnum.ADMIN} value={UserRolesEnum.ADMIN}>
                {UserRolesEnum.ADMIN}
              </MenuItem>
            </CustomSelect>
          </FormControl>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ mt: 2 }}>
            {t('common.save')}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};
